import {
  Directive,
  ElementRef,
  forwardRef,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Renderer2,
  SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export type B3DsInputSearchBackground = 'white' | 'grey';

type B3DsInputSearchElement = HTMLElement & {
  value?: string;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  background?: B3DsInputSearchBackground;
  id?: string;
  searchAriaLabel?: string;
  closeAriaLabel?: string;
};

@Directive({
  selector: 'b3-input-search[formControlName], b3-input-search[ngModel]',
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => B3DsInputSearchDirective),
      multi: true,
    },
  ],
})
export class B3DsInputSearchDirective implements ControlValueAccessor, OnInit, OnChanges {
  @Input() label = '';
  @Input() placeholder = '';
  @Input() background: B3DsInputSearchBackground = 'grey';
  @Input() inputId = '';
  @Input() disabled = false;
  @Input() searchAriaLabel = '';
  @Input() closeAriaLabel = '';

  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private value = '';
  private formDisabled = false;

  constructor(
    private readonly elementRef: ElementRef<B3DsInputSearchElement>,
    private readonly renderer: Renderer2,
  ) {}

  ngOnInit(): void {
    this.syncElementState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['label'] ||
      changes['placeholder'] ||
      changes['background'] ||
      changes['inputId'] ||
      changes['disabled'] ||
      changes['searchAriaLabel'] ||
      changes['closeAriaLabel']
    ) {
      this.syncElementState();
    }
  }

  writeValue(value: string | null | unknown): void {
    if (value == null) {
      this.value = '';
    } else if (typeof value === 'string') {
      this.value = value;
    } else if (typeof value === 'number' && Number.isFinite(value)) {
      this.value = String(value);
    } else {
      this.value = '';
    }

    this.syncElementState();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.formDisabled = isDisabled;
    this.syncElementState();
  }

  @HostListener('b3-input', ['$event'])
  onB3Input(event: Event): void {
    const customEvent = event as CustomEvent<{ value?: string }>;
    this.value = customEvent.detail?.value ?? '';
    this.syncElementState();
    this.onChange(this.value);
  }

  @HostListener('b3-change', ['$event'])
  onB3Change(event: Event): void {
    const customEvent = event as CustomEvent<{ value?: string }>;
    this.value = customEvent.detail?.value ?? '';
    this.syncElementState();
    this.onChange(this.value);
    this.onTouched();
  }

  @HostListener('b3-clear')
  onB3Clear(): void {
    this.value = '';
    this.syncElementState();
    this.onChange(this.value);
    this.onTouched();
  }

  @HostListener('b3-blur')
  onB3Blur(): void {
    this.onTouched();
  }

  private syncElementState(): void {
    const element = this.elementRef.nativeElement;
    element.value = this.value;
    element.disabled = this.disabled || this.formDisabled;
    element.background = this.background;

    this.setOptionalAttribute(element, 'label', this.label);
    this.setOptionalAttribute(element, 'placeholder', this.placeholder);
    this.setOptionalAttribute(element, 'background', this.background);
    this.setOptionalAttribute(element, 'search-aria-label', this.searchAriaLabel);
    this.setOptionalAttribute(element, 'close-aria-label', this.closeAriaLabel);

    if (this.inputId) {
      element.id = this.inputId;
      this.renderer.setAttribute(element, 'id', this.inputId);
    }
  }

  private setOptionalAttribute(element: B3DsInputSearchElement, name: string, value: string): void {
    if (value) {
      this.renderer.setAttribute(element, name, value);
      return;
    }

    this.renderer.removeAttribute(element, name);
  }
}
